import {
	I18nOnlyOnMainForm,
	I18nSlateTextInputField,
	I18nTextField,
	ImagePickerInput,
	LanguageMultiSelectField,
	LanguageResource,
	LanguageSingleSelectField,
	LocaleProvider,
	SingleChangeEvent,
	SingleSelectField,
} from "@dgs/core";
import { useField } from "formik";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EventCostCategoryResource, EventVisibility, visibilityOptions } from "../../types/event";
import { EventTypeSingleSelectField } from "../../ui/EventTypeSingleSelectField";
import { OrganizerSingleSelect } from "../../ui/OrganizerSingleSelect";
import { CreatableLocationField } from "./CreatableLocationField";
import { EventDateTimeFields } from "./EventDateTimeFields";
import { EventFormColumns } from "./EventFormColumns";
import { useEventTypeField } from "./useEventTypeField";

export const EventBaseDataStepFormFields: FC<{ eventTypeDisabled?: boolean }> = ({ eventTypeDisabled = false }) => {
	const { t } = useTranslation();
	const eventType = useEventTypeField();
	const [{ value: eventCostCategories }, , { setValue: setEventCostCategories }] =
		useField<EventCostCategoryResource[]>("eventCostCategories");
	const [{ value: timezone }] = useField<string>("timezone");
	const [{ value: visibility }, , { setValue: setVisibility }] = useField<EventVisibility>("visibility");
	const [{ value: primaryLanguage }] = useField<LanguageResource | null>("primaryLanguage");
	const [{ value: languages }, , { setValue: setLanguages }] = useField<LanguageResource[]>("languages");

	const allowedVisibilities = eventType?.allowedVisibilities?.length
		? visibilityOptions.filter((v) => (eventType.allowedVisibilities as string[]).includes(v))
		: visibilityOptions;

	useEffect(() => {
		if (!allowedVisibilities.includes(visibility)) {
			setVisibility(allowedVisibilities[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventType?.id]);

	return (
		<EventFormColumns>
			<I18nTextField name="name" label={t("Name")} required={true} autoFocus={true} />
			<I18nOnlyOnMainForm>
				<EventTypeSingleSelectField
					name="eventType"
					label={t("Event type")}
					required={true}
					disabled={eventTypeDisabled}
					onChange={(e) =>
						setEventCostCategories(
							e.target.value
								? [...e.target.value.costCategoryRequirements]
										.filter((item) => item.requirement !== "prohibited")
										.map(
											(item) =>
												eventCostCategories.find((c) => c.costCategory.id === item.costCategory.id) ?? {
													costCategory: item.costCategory,
													plannedCost: null,
													actualCost: null,
													commentAllowed: false,
													comment: null,
												},
										)
								: [],
						)
					}
				/>
				<LocaleProvider initialTimeZone={timezone}>
					<EventDateTimeFields />
				</LocaleProvider>
				<LanguageSingleSelectField
					name="primaryLanguage"
					label={t("Primary language")}
					required={true}
					onChange={
						((event: SingleChangeEvent<LanguageResource | null>) => {
							const primaryLanguage = event.target.value;
							if (primaryLanguage && languages.some((l) => l.isoCode === primaryLanguage.isoCode)) {
								setLanguages(languages.filter((l) => l.isoCode !== primaryLanguage.isoCode));
							}
						}) as any
					}
				/>
				{eventType?.eventLanguageRequirement !== "prohibited" && (
					<LanguageMultiSelectField
						name="languages"
						label={t("Additional languages")}
						required={eventType?.eventLanguageRequirement === "required"}
						exclude={primaryLanguage ? [primaryLanguage.isoCode] : undefined}
					/>
				)}
				{eventType?.locationRequirement !== "prohibited" && (
					<CreatableLocationField
						name="location"
						label={t("Location")}
						clearable={eventType?.locationRequirement === "optional"}
						required={eventType?.locationRequirement === "required"}
					/>
				)}
				{eventType?.organizerRequirement !== "prohibited" && (
					<OrganizerSingleSelect
						name="organizer"
						label={t("Organizer")}
						required={eventType?.organizerRequirement === "required"}
					/>
				)}
				<SingleSelectField
					name="visibility"
					label={t("Visibility")}
					required={true}
					options={allowedVisibilities}
					getLabel={(v) => t(`eventVisibility.${v}`)}
					getValue={(v) => v}
					disabled={allowedVisibilities.length === 1}
				/>
				<ImagePickerInput
					label={t("Image")}
					name="image"
					cropImageWithAspect={4}
					aspectRatioOptions={[{ name: "4 / 1", aspect: 4 }]}
					canRemoveImage={true}
				/>
			</I18nOnlyOnMainForm>
			{eventType?.additionalInfoRequirement !== "prohibited" && (
				<I18nSlateTextInputField
					name="additionalInfo"
					label={t("Additional information")}
					required={eventType?.additionalInfoRequirement === "required"}
				/>
			)}
		</EventFormColumns>
	);
};
